import { useMutation } from '@tanstack/react-query';

import { queryClient, withGlobalError } from '@/react-tools/react-query';
import { EnvironmentId } from '@/react/portainer/environments/types';
import axios, { parseAxiosError } from '@/portainer/services/axios';
import { notifySuccess } from '@/portainer/services/notifications';

import { Registry, RegistryTypes } from '../../types/registry';

import { queryKeys } from './queryKeys';

interface RemoveTagsParams {
  registry: Registry;
  repository: string;
  selectedTagNames: string[];
  deletingAll: boolean;
  endpointId?: EnvironmentId;
}

async function removeTags({
  registry,
  repository,
  selectedTagNames,
  endpointId,
}: RemoveTagsParams) {
  const encodedRepository = encodeURIComponent(repository);

  const isECR = registry.Type === RegistryTypes.ECR;
  const url = isECR
    ? `/registries/${registry.Id}/ecr/repositories/${encodedRepository}/tags`
    : `/registries/${registry.Id}/repositories/${encodedRepository}/tags`;
  const data = { tags: selectedTagNames };

  try {
    await axios.delete(url, {
      data,
      params: { endpointId },
    });
  } catch (err) {
    throw parseAxiosError(err);
  }
}

export function useRemoveTags() {
  const mutation = useMutation(removeTags, {
    onSuccess: (_, deleteTagsVars) => {
      notifySuccess('Success', 'Tags successfully deleted');

      // Only invalidate the tags list if there are still tags remaining.
      // Otherwise the tags list tries to refresh but the repository no longer exists.
      if (!deleteTagsVars.deletingAll) {
        queryClient.invalidateQueries(
          queryKeys.repositoryTags(
            deleteTagsVars.registry.Id,
            deleteTagsVars.repository
          )
        );
      }
    },
    ...withGlobalError('Unable to delete tags'),
  });

  return mutation;
}
